/**
 * 成果物（sandbox iframe）からのファイルダウンロード要求を親ページ側で検証・実行する。
 *
 * iframe に `allow-downloads` を付与すると成果物 JS がユーザー操作なしに任意内容・任意名の
 * ファイルを閲覧者のディスクへ書けるため、sandbox 属性は変えずに postMessage の一方向チャネル
 * で親（信頼済みコード）が allowlist・サイズ上限・ファイル名の検証とユーザー確認を強制する
 * （docs/features/custom-dashboard-security.md）。
 */
import { convertCsvToXlsx } from './csv-to-xlsx';
import {
  PDF_MAX_PAGES,
  PDF_ORIENTATIONS,
  PDF_PAGE_IMAGE_MAX_SIDE_PX,
  type PdfOrientation,
  convertImagesToPdf,
  readPngSize,
} from './images-to-pdf';

/** 保存を許可する拡張子と、親が確定する MIME type。子の申告 MIME は信用しない。 */
export const DASHBOARD_DOWNLOAD_MIME_BY_EXTENSION: ReadonlyMap<string, string> = new Map([
  ['csv', 'text/csv'],
  ['json', 'application/json'],
  ['png', 'image/png'],
  ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
]);

/** ダウンロード 1 件あたりのサイズ上限（bytes）。PDF の要求ではページ画像の合計に適用する。 */
export const DASHBOARD_DOWNLOAD_MAX_BYTES = 50 * 1024 * 1024;

const PDF_MIME = 'application/pdf';

const FILENAME_MAX_LENGTH = 200;
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>| -]/g;

/** 成果物が渡したバイト列を保存する要求（`sazaeDashboard.download`）。 */
export interface DashboardFileDownloadRequest {
  filename: string;
  mimeType: string;
  bytes: ArrayBuffer;
  /** true のとき `bytes` は成果物が渡した CSV で、保存時に xlsx へ変換する。 */
  convertFromCsv: boolean;
}

/** 成果物が渡したページ画像（PNG）から親が PDF を組み立てて保存する要求（`sazaeDashboard.exportPdf`）。 */
export interface DashboardPdfDownloadRequest {
  filename: string;
  mimeType: typeof PDF_MIME;
  pages: ArrayBuffer[];
  orientation: PdfOrientation;
}

export type DashboardDownloadRequest = DashboardFileDownloadRequest | DashboardPdfDownloadRequest;

export const isPdfDownloadRequest = (
  request: DashboardDownloadRequest,
): request is DashboardPdfDownloadRequest => 'pages' in request;

export type DashboardDownloadRejection =
  | 'invalid_message'
  | 'too_large'
  | 'invalid_filename'
  | 'extension_not_allowed'
  | 'xlsx_extension_required'
  | 'pdf_extension_required'
  | 'invalid_pdf_pages';

export type DashboardDownloadNormalization =
  | { ok: true; request: DashboardDownloadRequest }
  | { ok: false; reason: DashboardDownloadRejection };

interface DownloadMessageBody {
  filename?: unknown;
  payload?: unknown;
  xlsx?: unknown;
  pdf?: unknown;
}

// postMessage で届く ArrayBuffer は別 realm（jsdom テストや将来の Worker）由来でも受理できるよう
// instanceof ではなくタグで判定する。ArrayBufferView（Uint8Array 等）は対象外のまま。
const isArrayBuffer = (value: unknown): value is ArrayBuffer =>
  Object.prototype.toString.call(value) === '[object ArrayBuffer]';

// 区切りはちょうど 1 つだけ許可し、二重拡張子（report.csv.exe 等）を排除する。
const splitFilename = (filename: string): { base: string; extension: string } | null => {
  const sanitized = filename.replace(UNSAFE_FILENAME_CHARS, '_').trim();
  if (sanitized.length === 0 || sanitized.length > FILENAME_MAX_LENGTH) return null;
  const parts = sanitized.split('.');
  if (parts.length !== 2) return null;
  const base = parts[0].trim();
  if (base.length === 0) return null;
  return { base, extension: parts[1].trim().toLowerCase() };
};

// 親がデコードする前に、PNG であることと大きさを IHDR だけで確かめる。
const isAcceptablePageImage = (page: ArrayBuffer): boolean => {
  const size = readPngSize(page);
  return (
    size !== null &&
    [size.width, size.height].every((side) => side >= 1 && side <= PDF_PAGE_IMAGE_MAX_SIDE_PX)
  );
};

const normalizePdfDownload = (body: DownloadMessageBody): DashboardDownloadNormalization => {
  const pdf = body.pdf as { orientation?: unknown; pages?: unknown } | null;
  if (
    typeof body.filename !== 'string' ||
    body.payload !== undefined ||
    body.xlsx !== undefined ||
    typeof pdf !== 'object' ||
    pdf === null ||
    !PDF_ORIENTATIONS.includes(pdf.orientation as PdfOrientation) ||
    !Array.isArray(pdf.pages) ||
    !pdf.pages.every(isArrayBuffer)
  ) {
    return { ok: false, reason: 'invalid_message' };
  }
  const pages: ArrayBuffer[] = pdf.pages;
  if (pages.length === 0 || pages.length > PDF_MAX_PAGES || !pages.every(isAcceptablePageImage)) {
    return { ok: false, reason: 'invalid_pdf_pages' };
  }
  if (pages.reduce((total, page) => total + page.byteLength, 0) > DASHBOARD_DOWNLOAD_MAX_BYTES) {
    return { ok: false, reason: 'too_large' };
  }
  const name = splitFilename(body.filename);
  if (name === null) return { ok: false, reason: 'invalid_filename' };
  if (name.extension !== 'pdf') return { ok: false, reason: 'pdf_extension_required' };
  return {
    ok: true,
    request: {
      filename: `${name.base}.pdf`,
      mimeType: PDF_MIME,
      pages,
      orientation: pdf.orientation as PdfOrientation,
    },
  };
};

/**
 * 子からの `dashboard-download` message 本文を検証して保存要求に正規化する。
 * `pdf` を持つ message は PDF の要求（`sazaeDashboard.exportPdf`）として検証する。
 * kind / nonce / source の検証は呼び出し側（`attachDashboardDownloadReceiver`）が行う。
 */
export const normalizeDashboardDownload = (message: unknown): DashboardDownloadNormalization => {
  const body = message as DownloadMessageBody | null;
  if (body?.pdf !== undefined) return normalizePdfDownload(body);
  if (
    typeof body?.filename !== 'string' ||
    !isArrayBuffer(body.payload) ||
    (body.xlsx !== undefined && typeof body.xlsx !== 'boolean')
  ) {
    return { ok: false, reason: 'invalid_message' };
  }
  if (body.payload.byteLength > DASHBOARD_DOWNLOAD_MAX_BYTES) {
    return { ok: false, reason: 'too_large' };
  }
  const name = splitFilename(body.filename);
  if (name === null) {
    return { ok: false, reason: 'invalid_filename' };
  }
  const { base, extension } = name;
  // PDF はページ画像から親が組み立てるものだけを保存し、子のバイト列をそのまま保存しないため allowlist に無い。
  const mimeType = DASHBOARD_DOWNLOAD_MIME_BY_EXTENSION.get(extension);
  if (mimeType === undefined) {
    return { ok: false, reason: 'extension_not_allowed' };
  }
  // 保存されるファイル名と中身の形式を一致させるため、変換指定は保存名が .xlsx のときだけ受け付ける。
  const convertFromCsv = body.xlsx === true;
  if (convertFromCsv && extension !== 'xlsx') {
    return { ok: false, reason: 'xlsx_extension_required' };
  }
  return {
    ok: true,
    request: { filename: `${base}.${extension}`, mimeType, bytes: body.payload, convertFromCsv },
  };
};

/**
 * iframe からの `dashboard-download` message を受け取り、検証済みの要求だけを `onRequest` に渡す。
 * 拒否した要求は成果物作成者が原因に気付けるよう `console.warn` に理由を出す。
 * 保存はここでは行わない（ユーザー確認を経て `saveDashboardDownload` を呼ぶのは呼び出し側）。
 */
export const attachDashboardDownloadReceiver = (
  iframe: HTMLIFrameElement,
  nonce: string,
  onRequest: (request: DashboardDownloadRequest) => void,
): (() => void) => {
  const handleMessage = (event: MessageEvent<unknown>) => {
    const targetWindow = iframe.contentWindow;
    if (!targetWindow || event.source !== targetWindow) return;
    const message = event.data as { kind?: unknown; nonce?: unknown } | null;
    if (message?.kind !== 'dashboard-download' || message.nonce !== nonce) return;
    const normalized = normalizeDashboardDownload(message);
    if (!normalized.ok) {
      console.warn('ダッシュボードからのダウンロード要求を拒否しました', {
        reason: normalized.reason,
      });
      return;
    }
    onRequest(normalized.request);
  };
  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};

/**
 * 検証済みの要求をブラウザのダウンロードとして保存する。ユーザー操作のハンドラから呼ぶこと。
 * CSV からの変換は閲覧者の操作なしに成果物が親へ重い処理をさせないよう、受信時ではなくここで行う。
 * 変換できない場合は保存せずに `XlsxConversionError` を投げる。
 */
export const saveDashboardDownload = ({
  filename,
  mimeType,
  bytes,
  convertFromCsv,
}: DashboardFileDownloadRequest): void => {
  const content = convertFromCsv ? convertCsvToXlsx(bytes) : bytes;
  saveBlob(new Blob([content], { type: mimeType }), filename);
};

/**
 * 検証済みの PDF 要求のページ画像から PDF を組み立てて保存する。ユーザー操作のハンドラから呼ぶこと。
 * 組み立ては受信時ではなくここで行う（閲覧者の操作なしに成果物が親へ重い処理をさせない）。
 * 組み立てられない場合は保存せずに `PdfConversionError` で reject する。
 */
export const saveDashboardPdf = async ({
  filename,
  mimeType,
  pages,
  orientation,
}: DashboardPdfDownloadRequest): Promise<void> => {
  const pdf = await convertImagesToPdf(pages, orientation);
  saveBlob(new Blob([pdf], { type: mimeType }), filename);
};

const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
};

/** 確認ダイアログ表示用にサイズを人が読める単位へ整形する。 */
export const formatDownloadSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
